import React, { useState } from "react";
import {
  Search,
  Loader,
  Server,
  AlertTriangle,
  RefreshCw,
  Check,
  Download,
  FileSpreadsheet,
} from "lucide-react";
import {
  generateMigrationReport,
  createDownloadLink,
} from "../../../utils/ExcelReportGenerator";

const AnalysisGapDetection = ({
  setAnalysisFullyComplete,
  analysisFullyComplete,
  questionnaireData = {},
}) => {
  const [analyzing, setAnalyzing] = useState(false);
  const [analysisComplete, setAnalysisComplete] = useState(false);
  const [progress, setProgress] = useState(0);
  const [gapAnalysis, setGapAnalysis] = useState([]);
  const [gapResponses, setGapResponses] = useState({
    benefits: "",
    hierarchy: "",
    jobCodes: "",
  });

  // New states for the Excel report
  const [reportGenerated, setReportGenerated] = useState(false);
  const [reportUrl, setReportUrl] = useState(null);
  const [generatingReport, setGeneratingReport] = useState(false);

  const startAnalysis = () => {
    setAnalyzing(true);
    setProgress(0);

    // Simulate gap findings and progress
    const progressInterval = setInterval(() => {
      setProgress((prevProgress) => {
        const increment = Math.floor(Math.random() * 10) + 5;
        const newProgress = Math.min(prevProgress + increment, 100);
        return newProgress;
      });
    }, 500);

    // Set timeout for completion
    setTimeout(() => {
      clearInterval(progressInterval);
      setProgress(100);
      setAnalyzing(false);
      setAnalysisComplete(true);
      setGapAnalysis([
        {
          field: "Employee Benefits",
          description: "Missing mapping for custom benefit types",
          severity: "high",
          recommendation:
            "Please provide details on custom benefit codes and their meanings",
        },
        {
          field: "Department Hierarchy",
          description: "Inconsistent hierarchy depth across departments",
          severity: "medium",
          recommendation:
            "Standardize department hierarchy or provide mapping rules",
        },
        {
          field: "Job Codes",
          description: "Several job codes without descriptions",
          severity: "low",
          recommendation:
            "Review and provide descriptions for missing job codes",
        },
      ]);
    }, 5000);
  };

  const continueAnalysis = () => {
    // Simulate continuing the analysis with the provided information
    setAnalyzing(true);

    // Simulate completion after a short delay
    setTimeout(() => {
      setAnalyzing(false);
      setAnalysisFullyComplete(true);
    }, 2000);
  };

  const handleGenerateReport = () => {
    setGeneratingReport(true);

    // Simulate report generation delay
    setTimeout(() => {
      // Create mock analysis data
      const analysisData = {
        issuesCount: 5,
        resolvedCount: 5,
        estimatedCompletionTime: "12 weeks",
        migrationComplexity: "Medium",
        riskAreas: [
          "Custom Benefits",
          "Department Hierarchy",
          "Location Types",
          "Management Levels",
        ],
        recommendations: [
          "Standardize department hierarchy",
          "Document all benefit code mappings",
          "Review job code descriptions",
          "Clarify management level definitions",
        ],
      };

      // Generate the Excel report
      const reportBlob = generateMigrationReport(
        analysisData,
        questionnaireData
      );

      // Create a download URL
      const downloadUrl = createDownloadLink(reportBlob);

      // Update state with the URL
      setReportUrl(downloadUrl);
      setReportGenerated(true);
      setGeneratingReport(false);
    }, 3000);
  };

  return (
    <div className="mt-6 space-y-6">
      <h3 className="text-lg font-medium text-gray-900">Discovery Analysis</h3>

      {!analyzing && !analysisComplete && !analysisFullyComplete && (
        <div className="text-center py-6">
          <Server className="mx-auto h-12 w-12 text-gray-400" />
          <h3 className="mt-2 text-sm font-medium text-gray-900">
            Ready to begin analysis
          </h3>
          <p className="mt-1 text-sm text-gray-500">
            Our AI will scan your system structure and data to identify
            potential migration gaps.
          </p>
          <div className="mt-6">
            <button
              type="button"
              className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
              onClick={startAnalysis}
            >
              <Search className="mr-2 -ml-1" size={16} />
              Start Analysis
            </button>
          </div>
        </div>
      )}

      {analyzing && (
        <div className="text-center py-10">
          <div className="mx-auto h-12 w-12 text-blue-600">
            <Loader className="animate-spin" size={48} />
          </div>
          <h3 className="mt-2 text-sm font-medium text-gray-900">
            Discovery in progress
          </h3>
          <p className="mt-1 text-sm text-gray-500">
            Analyzing database structure, fields, and data patterns...
          </p>
          <div className="mt-6 max-w-2xl mx-auto">
            <div className="w-full bg-gray-200 rounded-full h-2.5 mb-2">
              <div
                className="bg-blue-600 h-2.5 rounded-full transition-all duration-300 ease-out"
                style={{ width: `${progress}%` }}
              ></div>
            </div>
            <p className="text-sm text-gray-600">{progress}% Complete</p>
          </div>
        </div>
      )}

      {analysisComplete && !analysisFullyComplete && (
        <div>
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-medium text-gray-900">
              Gap Analysis Results
            </h3>
            <span className="px-2 py-1 text-xs font-medium rounded-full bg-yellow-100 text-yellow-800">
              {gapAnalysis.length} issues found
            </span>
          </div>

          <div className="space-y-4 mb-8">
            {gapAnalysis.map((gap, index) => (
              <div key={index} className="border rounded-lg overflow-hidden">
                <div
                  className={`px-4 py-2 flex items-center justify-between ${
                    gap.severity === "high"
                      ? "bg-red-50"
                      : gap.severity === "medium"
                      ? "bg-yellow-50"
                      : "bg-blue-50"
                  }`}
                >
                  <div className="flex items-center">
                    <AlertTriangle
                      className={`mr-2 ${
                        gap.severity === "high"
                          ? "text-red-500"
                          : gap.severity === "medium"
                          ? "text-yellow-500"
                          : "text-blue-500"
                      }`}
                      size={16}
                    />
                    <span className="font-medium">{gap.field}</span>
                  </div>
                  <span
                    className={`text-xs px-2 py-1 rounded-full ${
                      gap.severity === "high"
                        ? "bg-red-100 text-red-800"
                        : gap.severity === "medium"
                        ? "bg-yellow-100 text-yellow-800"
                        : "bg-blue-100 text-blue-800"
                    }`}
                  >
                    {gap.severity} priority
                  </span>
                </div>
                <div className="p-4 bg-white">
                  <p className="text-sm text-gray-700 mb-2">
                    {gap.description}
                  </p>
                  <p className="text-sm text-gray-500">
                    <span className="font-medium">Recommendation:</span>{" "}
                    {gap.recommendation}
                  </p>
                </div>
              </div>
            ))}
          </div>

          <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
            <h4 className="font-medium text-yellow-800 mb-2 flex items-center">
              <RefreshCw className="mr-2" size={16} />
              Additional Information Required
            </h4>
            <p className="text-sm text-yellow-700 mb-4">
              Based on our analysis, we need additional information to complete
              the discovery process. Please provide details for each identified
              issue:
            </p>

            <div className="space-y-4">
              <div className="p-4 border border-yellow-300 rounded-lg bg-white">
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Employee Benefits: Please describe your custom benefit codes
                  and their meanings
                </label>
                <textarea
                  className="w-full p-2 border border-gray-300 rounded h-16"
                  placeholder="Enter your response here"
                  value={gapResponses.benefits}
                  onChange={(e) =>
                    setGapResponses((prev) => ({
                      ...prev,
                      benefits: e.target.value,
                    }))
                  }
                />
                <div className="mt-2">
                  <button
                    type="button"
                    className="px-2 py-1 text-xs font-medium rounded bg-gray-100 text-gray-700 hover:bg-gray-200"
                    onClick={() =>
                      setGapResponses((prev) => ({
                        ...prev,
                        benefits:
                          "MEDBEN: Medical Benefits (HMO/PPO), DENBEN: Dental Benefits, VISBEN: Vision Benefits, LIFBEN: Life Insurance, DISBEN: Disability Insurance",
                      }))
                    }
                  >
                    Use Sample Data
                  </button>
                </div>
              </div>

              <div className="p-4 border border-yellow-300 rounded-lg bg-white">
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Department Hierarchy: Please provide hierarchy standardization
                  details
                </label>
                <textarea
                  className="w-full p-2 border border-gray-300 rounded h-16"
                  placeholder="Enter your response here"
                  value={gapResponses.hierarchy}
                  onChange={(e) =>
                    setGapResponses((prev) => ({
                      ...prev,
                      hierarchy: e.target.value,
                    }))
                  }
                />
                <div className="mt-2">
                  <button
                    type="button"
                    className="px-2 py-1 text-xs font-medium rounded bg-gray-100 text-gray-700 hover:bg-gray-200"
                    onClick={() =>
                      setGapResponses((prev) => ({
                        ...prev,
                        hierarchy:
                          "Standard hierarchy: Division > Department > Team > Role. Maximum depth: 4 levels. All existing departments will be mapped to this structure.",
                      }))
                    }
                  >
                    Use Sample Data
                  </button>
                </div>
              </div>

              <div className="p-4 border border-yellow-300 rounded-lg bg-white">
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Job Codes: Please provide descriptions for missing job codes
                </label>
                <textarea
                  className="w-full p-2 border border-gray-300 rounded h-16"
                  placeholder="Enter your response here"
                  value={gapResponses.jobCodes}
                  onChange={(e) =>
                    setGapResponses((prev) => ({
                      ...prev,
                      jobCodes: e.target.value,
                    }))
                  }
                />
                <div className="mt-2">
                  <button
                    type="button"
                    className="px-2 py-1 text-xs font-medium rounded bg-gray-100 text-gray-700 hover:bg-gray-200"
                    onClick={() =>
                      setGapResponses((prev) => ({
                        ...prev,
                        jobCodes:
                          "JC500: Senior Developer, JC501: Principal Engineer, JC502: Technical Architect, JC600: Product Manager, JC601: Program Manager",
                      }))
                    }
                  >
                    Use Sample Data
                  </button>
                </div>
              </div>
            </div>

            <div className="mt-4">
              <button
                type="button"
                className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-yellow-600 hover:bg-yellow-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-yellow-500"
                onClick={continueAnalysis}
                disabled={analyzing}
              >
                {analyzing ? (
                  <>
                    <Loader className="animate-spin mr-2" size={16} />
                    Processing...
                  </>
                ) : (
                  <>
                    <RefreshCw className="mr-2 -ml-1" size={16} />
                    Continue Analysis
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {analysisFullyComplete && (
        <div className="text-center py-10">
          <div className="mx-auto flex items-center justify-center w-24 h-24 rounded-full bg-green-100">
            <Check className="h-16 w-16 text-green-600" />
          </div>
          <h3 className="mt-6 text-xl font-medium text-gray-900">
            Analysis Complete
          </h3>
          <p className="mt-2 text-base text-gray-600 max-w-md mx-auto">
            We've successfully analyzed your data and incorporated your
            additional information. Your migration path has been mapped and is
            ready for the next steps.
          </p>
          <div className="mt-8 space-y-4">
            <div className="bg-green-50 border border-green-200 rounded-lg p-4 max-w-md mx-auto text-left">
              <h4 className="font-medium text-green-800 mb-2">
                Key Findings Summary
              </h4>
              <ul className="text-sm text-green-700 space-y-2">
                <li className="flex items-start">
                  <Check className="h-5 w-5 text-green-500 mr-2 shrink-0" />
                  <span>All custom benefit codes mapped successfully</span>
                </li>
                <li className="flex items-start">
                  <Check className="h-5 w-5 text-green-500 mr-2 shrink-0" />
                  <span>Department hierarchy standardized to 4 levels</span>
                </li>
                <li className="flex items-start">
                  <Check className="h-5 w-5 text-green-500 mr-2 shrink-0" />
                  <span>All job codes identified and described</span>
                </li>
                <li className="flex items-start">
                  <Check className="h-5 w-5 text-green-500 mr-2 shrink-0" />
                  <span>Location types and usages standardized</span>
                </li>
                <li className="flex items-start">
                  <Check className="h-5 w-5 text-green-500 mr-2 shrink-0" />
                  <span>Management levels properly defined</span>
                </li>
                <li className="flex items-start">
                  <Check className="h-5 w-5 text-green-500 mr-2 shrink-0" />
                  <span>Data transformation rules defined</span>
                </li>
              </ul>
            </div>

            <div className="mt-8">
              {!reportGenerated ? (
                <button
                  type="button"
                  className={`inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 ${
                    generatingReport ? "opacity-75 cursor-not-allowed" : ""
                  }`}
                  onClick={handleGenerateReport}
                  disabled={generatingReport}
                >
                  {generatingReport ? (
                    <>
                      <Loader className="animate-spin mr-2" size={16} />
                      Generating Excel Report...
                    </>
                  ) : (
                    <>
                      <FileSpreadsheet className="mr-2 -ml-1" size={16} />
                      Generate Excel Report
                    </>
                  )}
                </button>
              ) : (
                <a
                  href={reportUrl}
                  download="Nexus_Migration_Analysis_Report.xlsx"
                  className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-green-600 hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500"
                >
                  <Download className="mr-2 -ml-1" size={16} />
                  Download Excel Report
                </a>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AnalysisGapDetection;
